import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import {
  Modal,
  ModalProps,
  Space,
  Popconfirm,
  Button,
  Input,
  Spin,
  Row,
  Col,
  message,
  Switch,
} from 'antd';
import {
  CloseOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons';
import {
  ShopeeMultiAuthShop,
  ShopeeMultiAuthShopRegion,
  ShopeeMultiAuthShopStatus,
} from 'services/api/uploads';
import FRadioGroup from 'components/FRadioGroup';
import useShopeeAuthShops, {
  AuthMode,
  AuthShopsStatus,
  authShopsStatus,
} from 'hooks/business/authShops/useShopeeAuthShops';
import {
  isValidNumberForRegion,
  parse as phoneParser,
  format as phoneFormatter,
  isSupportedCountry,
  isValidNumber,
} from 'libphonenumber-js';
import useAuthShopUtils from 'hooks/business/authShops/useShopeeUtils';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import AuthShopHelper from 'components/AuthShop/AuthShopHelper';
import posthog from '@leyan/lytics';
import { useAuthorization } from 'components/Authorization';
import { notifyAuthShopNeedHelp } from 'services/api/admin';
import DepartmentItemSelect from 'components/AuthShop/components/DepartmentItemSelect';
import MultiAuthXlsUpload from './MultiAuth/MultiAuthXlsUpload';
import Tips from './Tips';
import { useTranslate } from '@tolgee/react';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import {
  getShopeeCrossMultipleAuthTemplateFile,
  getShopeeLocalMultipleAuthTemplateFile,
} from 'services/i18n/utils';
import { useAtomValue } from 'jotai';

// 帐号系统
export enum AccountSystem {
  CNSC,
  Normal,
}

// 授权店铺
export enum AuthShopsMethod {
  All,
  Assign,
}

export interface StoreTypeModalProps extends Omit<ModalProps, 'children' | 'footer'> {
  initialValues?: {
    storeType?: IM_API.Admin.StoreType;
    region?: General.CaselessRegion | 'WORLD';
    authMode?: AuthMode;
    proxyMode?: boolean;
  };
  onChange?: (
    authMode: AuthMode,
    storeType: IM_API.Admin.StoreType,
    region: General.Region | 'WORLD',
    shops?: ShopeeMultiAuthShop[],
    remember?: boolean,
    departmentIds?: number[],
    proxyMode?: boolean,
  ) => any;
  isInitial?: boolean;
  loading?: boolean;
  onOk?: (e: any, isQuickMode?: boolean) => void;
}

const regions: General.Region[] = [
  'br',
  'id',
  'my',
  'ph',
  'sg',
  'th',
  'vn',
  'cl',
  'co',
  'mx',
  'pl',
  'es',
  'fr',
];

const StoreTypeModal = ({
  isInitial,
  initialValues: {
    storeType: initialStoreType = 'UNKNOWN',
    region: initialRegion,
    authMode: initialAuthMode,
  } = {},
  onChange,
  onCancel,
  onOk,
  ...otherProps
}: StoreTypeModalProps) => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [storeType, setStoreType] = useState<IM_API.Admin.StoreType>(initialStoreType);
  // const [authMode, setAuthMode] = useState(
  //   canMultiAuth && initialAuthMode === AuthMode.Multi ? AuthMode.Multi : AuthMode.Single,
  // );
  const [authMode] = useState(AuthMode.Multi); // 授权流程优化
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [region, setRegion] = useState<General.Region>(() =>
    initialRegion ? (initialRegion.toLowerCase() as General.Region) : 'id',
  );
  const [uploading, setUploading] = useState(false);
  const [multiShops, setMultiShops] = useState<ShopeeMultiAuthShop[]>([]);

  const [accountSystem, setAccountSystem] = useState(
    initialAuthMode === AuthMode.Multi ? AccountSystem.CNSC : AccountSystem.Normal,
  );
  const [authShops, setAuthShops] = useState(AuthShopsMethod.All);
  const [accountName, setAccountName] = useState('');
  const [password, setPassword] = useState('');
  const [singleAuthShopName, setSingleAuthShopName] = useState<string[]>([]);
  const authStatus = useAtomValue(authShopsStatus);
  const { canQuickMode } = useAuthShopUtils();
  const [multiAuthFooter, setMultiAuthFooter] = useState<React.ReactNode[]>([]);
  const { terminateAuth } = useShopeeAuthShops();
  const rememberPass = true;
  const [proxyMode, setProxyMode] = useState(true);

  // 单店授权加载中蒙层
  const loading = useMemo(() => {
    return (
      AuthMode.Single === authMode &&
      (authStatus === AuthShopsStatus.Pending || authStatus === AuthShopsStatus.Start)
    );
  }, [authMode, authStatus]);

  const getMultiShopsWhenSingleAuth = useCallback(() => {
    const isCnsc = accountSystem === AccountSystem.CNSC;
    return [
      {
        region: (isCnsc ? 'CNSC' : region.toUpperCase()) as ShopeeMultiAuthShopRegion,
        isCnsc,
        accountName,
        password,
        shopName: singleAuthShopName,
        authStatus: ShopeeMultiAuthShopStatus.None,
        isShopAll: authShops === AuthShopsMethod.All,
      },
    ];
  }, [accountName, accountSystem, authShops, password, region, singleAuthShopName]);

  useEffect(() => {
    if (!otherProps.visible) {
      setUploading(false);
      setMultiShops([]);
    }
  }, [otherProps.visible]);

  const handleOk = useCallback(
    (e) => {
      if (authMode === AuthMode.Single) {
        onChange &&
          onChange(
            authMode,
            storeType,
            region || 'WORLD',
            getMultiShopsWhenSingleAuth(),
            rememberPass,
            departmentIds,
            proxyMode,
          );
      }
      onOk && onOk(e);
    },
    [
      authMode,
      onOk,
      onChange,
      storeType,
      region,
      getMultiShopsWhenSingleAuth,
      rememberPass,
      departmentIds,
      proxyMode,
    ],
  );

  const formatAccount = useCallback(
    (value: string, region: General.Region) => {
      const upperRegion = region.toUpperCase() as General.UpperCaseRegion;
      if (
        value &&
        storeType === 'LOCAL' &&
        accountSystem !== AccountSystem.CNSC &&
        isSupportedCountry(upperRegion) &&
        (isValidNumber(value) || isValidNumberForRegion(value, upperRegion))
      ) {
        const phoneNumber = phoneParser(value, upperRegion);
        setAccountName(phoneFormatter(phoneNumber, 'E.164'));
      }
    },
    [accountSystem, storeType],
  );

  const regionSelect = useMemo(() => {
    return (
      <FormLine>
        <Label>{t('ViewShops.store.region')}:</Label>
        <div>
          {regions.map((item) => (
            <RegionTag
              className={region === item ? 'checked' : ''}
              key={item}
              onClick={() => {
                setRegion(item);
                formatAccount(accountName, item);
              }}
            >
              <img
                alt={item}
                src={`${process.env.PUBLIC_URL}/country-flag-icons/${item?.toUpperCase()}.svg`}
              />
              <span>{getRegionLabel(item)}</span>
            </RegionTag>
          ))}
        </div>
      </FormLine>
    );
  }, [accountName, formatAccount, region, t, getRegionLabel]);

  const systemLanguage = useSystemLanguageValue();

  const regionOrTipsOrUpload = useMemo(() => {
    const localDownloadUrl = getShopeeLocalMultipleAuthTemplateFile(systemLanguage);
    const crossDownloadUrl = getShopeeCrossMultipleAuthTemplateFile(systemLanguage);

    if (authMode === AuthMode.Multi) {
      return (
        <FormLine>
          <Label>
            {t('ViewShops.store.upload.file')}:
            <TemplateAlert align="end">
              <a download href={storeType === 'LOCAL' ? localDownloadUrl : crossDownloadUrl}>
                <DownloadOutlined />
                {storeType === 'LOCAL'
                  ? t('ViewShops.shopee.multi.auth.download.template.name.local')
                  : t('ViewShops.shopee.multi.auth.download.template.name.cross')}
              </a>
            </TemplateAlert>
          </Label>
          <div>
            <MultiAuthXlsUpload
              key={storeType}
              storeType={storeType}
              onChange={(allShops, data) => {
                setMultiShops(allShops);
                setUploading(data?.file.status === 'uploading');
                onChange &&
                  onChange(
                    authMode,
                    storeType,
                    region || 'WORLD',
                    allShops,
                    rememberPass,
                    departmentIds,
                    proxyMode,
                  );
              }}
            />
          </div>
        </FormLine>
      );
    }
    // 单个帐号授权，切换表单
    if (authMode === AuthMode.Single) {
      let diffComponent;
      if (storeType === 'LOCAL') {
        // 本土店铺，展示店铺类型
        diffComponent = regionSelect;
      }
      if (storeType === 'CROSS') {
        // 跨境店铺，展示帐号体系、授权店铺
        diffComponent = (
          <>
            <FormLine>
              <Label>{t('ViewShops.store.account.system')}</Label>
              <div>
                <FRadioGroup
                  value={accountSystem}
                  options={[
                    {
                      value: AccountSystem.CNSC,
                      label: 'CNSC',
                    },
                    {
                      value: AccountSystem.Normal,
                      label: t('ViewShops.store.account.system.normal'),
                    },
                  ]}
                  onChange={(value) => setAccountSystem(value as AccountSystem)}
                />
              </div>
            </FormLine>
            {accountSystem === AccountSystem.Normal ? regionSelect : null}
            <FormLine>
              <Label>{t('ViewShops.store.auth.shops')}:</Label>
              <div>
                <FRadioGroup
                  value={authShops}
                  options={[
                    {
                      value: AuthShopsMethod.All,
                      label: t('ViewShops.store.auth.shop.name.all'),
                    },
                    {
                      value: AuthShopsMethod.Assign,
                      label: t('ViewShops.store.auth.shop.name.assign'),
                    },
                  ]}
                  onChange={(value) => {
                    setAuthShops(value as AuthShopsMethod);
                    setSingleAuthShopName([]);
                  }}
                />
              </div>
            </FormLine>
          </>
        );
      }

      return (
        <>
          {diffComponent}
          <FormLine>
            <Label>{t('ViewShops.store.account.password')}</Label>
            <div>
              <AccountPwdTips>{t('ViewShops.tips.auth.modal.banner')}</AccountPwdTips>
              <Input
                className={accountInput}
                placeholder={t('ViewShops.store.input.placeholder.account')}
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                onBlur={(e) => {
                  const { value } = e.target;
                  formatAccount(value, region);
                }}
              />
              <Input.Password
                className={accountInput}
                placeholder={t('ViewShops.store.input.placeholder.password')}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </FormLine>
        </>
      );
    }
    return null;
  }, [
    authMode,
    storeType,
    onChange,
    region,
    rememberPass,
    t,
    accountName,
    password,
    regionSelect,
    accountSystem,
    authShops,
    formatAccount,
    systemLanguage,
    departmentIds,
    proxyMode,
  ]);

  const ready = useMemo(() => {
    const typeReady = storeType !== 'UNKNOWN';
    const authModeReady = authMode === AuthMode.Multi ? multiShops.length > 0 : true;
    // 批量授权\跨境店可以忽略站点选择
    const regionReady =
      authMode === AuthMode.Multi || storeType === 'CROSS' || (region && !/world/i.test(region));
    return typeReady && authModeReady && regionReady;
  }, [authMode, multiShops.length, region, storeType]);

  useEffect(() => {
    canQuickMode(multiShops).then((canQuick) => {
      const buttons = [
        <Button
          type={canQuick ? 'default' : 'primary'}
          onClick={(e) => {
            onOk && onOk(e);
          }}
        >
          {t('ViewShops.shopee.multi.interrupt.modal.confirm.btn.normal')}
        </Button>,
      ];
      if (canQuick) {
        buttons.push(
          <Button
            type="primary"
            onClick={(e) => {
              onOk && onOk(e, true);
            }}
          >
            {t('ViewShops.shopee.multi.interrupt.modal.confirm.btn.quick')}
          </Button>,
        );
      }
      setMultiAuthFooter(buttons);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiShops]);

  const handleNeedHelp = useCallback(async () => {
    posthog.capture('$authShopeeShops', {
      result: JSON.stringify({
        platform: 'shopee',
        orgId: payload?.orgId,
        phoneNumber: payload?.phoneNumber,
        entry: 'batch',
      }),
    });
    try {
      const data = await notifyAuthShopNeedHelp();
      if (data?.data?.code === 200) {
        message.success(t('authShop.已经安排客户服务专家联系您，请稍后'));
      } else {
        throw new Error('请求发送失败');
      }
    } catch (err) {
      message.error(t('authShop.请求发送失败'));
    }
  }, [payload?.orgId, payload?.phoneNumber, t]);

  const { closeIcon, footer } = useMemo(() => {
    const doClose = (e: any) => {
      onCancel && onCancel(e);
    };

    let closeIcon = <CloseOutlined onClick={doClose} />;
    let cancelBtn = <Button onClick={doClose}>{t('phrase.取消')}</Button>;
    if (uploading || multiShops.length > 0) {
      const title =
        multiShops.length > 0
          ? t('ViewShops.shopee.multi.auth.xls.uploaded.confirm.tips')
          : t('ViewShops.shopee.multi.auth.xls.uploading.confirm.tips');
      closeIcon = (
        <Popconfirm onConfirm={doClose} title={title}>
          <CloseOutlined />
        </Popconfirm>
      );
      cancelBtn = (
        <Popconfirm onConfirm={doClose} title={title}>
          <Button disabled={loading}>{t('phrase.取消')}</Button>
        </Popconfirm>
      );
    }

    if (loading) {
      closeIcon = (
        <Popconfirm
          onConfirm={terminateAuth}
          title={t('ViewShops.shopee.multi.auth.pending.cancel.tips')}
        >
          <CloseOutlined />
        </Popconfirm>
      );
      cancelBtn = (
        <Popconfirm
          onConfirm={terminateAuth}
          title={t('ViewShops.shopee.multi.auth.pending.cancel.tips')}
        >
          <Button>{t('phrase.取消')}</Button>
        </Popconfirm>
      );
    }

    return {
      closeIcon,
      footer: (
        <Row justify="space-between" align="middle">
          <Col>
            <AuthShopHelper display="tips" onNeedHelp={handleNeedHelp} />
          </Col>
          <Col>
            {authMode === AuthMode.Multi && multiShops.length > 0 ? (
              <Space>
                {cancelBtn}
                {multiAuthFooter}
              </Space>
            ) : (
              <Space>
                {cancelBtn}
                <Button type="primary" disabled={!ready || loading} onClick={handleOk}>
                  {t('ViewShops.modal.ok.go.auth')}
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      ),
    };
  }, [
    authMode,
    handleNeedHelp,
    handleOk,
    loading,
    multiAuthFooter,
    multiShops.length,
    onCancel,
    ready,
    t,
    terminateAuth,
    uploading,
  ]);

  const isValidStoreType = useMemo(() => storeType && storeType !== 'UNKNOWN', [storeType]);

  return (
    <>
      <AuthModal
        title={t('ViewShops.store.auth.modal.title')}
        width={560}
        okButtonProps={{ disabled: !ready }}
        {...otherProps}
        closeIcon={closeIcon}
        footer={footer}
      >
        <Spin spinning={loading} tip={t('ViewShops.store.auth.loading')}>
          <Tips />
          <AuthModalBody>
            <FormLine>
              <Label>{t('ViewShops.store.type')}:</Label>
              <div>
                <FRadioGroup
                  value={storeType}
                  onChange={(value) => {
                    const storeType = value as IM_API.Admin.StoreType;
                    setStoreType(storeType);
                    setSingleAuthShopName([]);
                    if (authMode === AuthMode.Single && storeType === 'CROSS') {
                      setAccountSystem(AccountSystem.CNSC);
                    } else {
                      setAccountSystem(AccountSystem.Normal);
                    }
                    const finalRegion = storeType === 'CROSS' ? 'WORLD' : region;
                    if (!finalRegion) {
                      return;
                    }
                    onChange &&
                      onChange(
                        authMode,
                        value as IM_API.Admin.StoreType,
                        finalRegion,
                        multiShops,
                        rememberPass,
                        departmentIds,
                        proxyMode,
                      );
                  }}
                  options={[
                    {
                      value: 'LOCAL',
                      label: t('ViewShops.LOCAL'),
                    },
                    {
                      value: 'CROSS',
                      label: t('ViewShops.store.type.cross'),
                    },
                  ]}
                />
              </div>
            </FormLine>
            <MarginTop>
              <DepartmentItemSelect
                platform="shopee"
                showRequiredMark={false}
                value={departmentIds}
                onChange={(ids) => setDepartmentIds(ids)}
              />
            </MarginTop>
            {isValidStoreType && <Part>{regionOrTipsOrUpload}</Part>}
            <FormLine>
              <Label>{t('代理模式')}:</Label>
              <Switch
                checked={proxyMode}
                onChange={(checked) => {
                  setProxyMode(checked);
                  onChange &&
                    onChange(
                      authMode,
                      storeType,
                      region,
                      multiShops,
                      rememberPass,
                      departmentIds,
                      checked,
                    );
                }}
              />
            </FormLine>
          </AuthModalBody>
        </Spin>
      </AuthModal>
    </>
  );
};

const AuthModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
`;
const AuthModalBody = styled.div`
  padding: 16px 32px;

  .f-radio-group-item {
    max-width: 100%;
  }
`;
const FormLine = styled.div`
  width: 100%;
  margin: 8px 0;
  flex: 0 0 1;
  > div {
    min-width: 5.5em;
    line-height: 1.5em;
  }

  > div:not(:first-child) {
    width: 100%;
  }
`;
const Label = styled.div`
  height: 32px;
  line-height: 32px;
  display: flex;
  font-weight: 500;
  justify-content: space-between;
  align-items: center;
  flex: 1 1;
  > * {
    flex-basis: 300px;
  }
`;
const TemplateAlert = styled(Space)`
  width: 100%;
  padding: 8px 0;
  line-height: 1;
  justify-content: end;
`;
const Part = styled.div`
  margin: 6px 0;
`;
const RegionTag = styled.div`
  display: inline-block;
  height: 32px;
  line-height: 32px;
  padding: 0 10px 0 8px;
  margin-bottom: 16px;
  margin-right: 16px;
  border: 1px solid rgba(211, 215, 224, 1);
  cursor: pointer;

  &:hover,
  &.checked {
    border-color: var(--ant-primary-color);
    color: var(--ant-primary-color);
  }

  & > img {
    width: 16px;
    margin-right: 6px;
    vertical-align: baseline;
  }
`;
const accountInput = css`
  width: 260px;
  margin-bottom: 8px;
`;
const AccountPwdTips = styled.div`
  height: 32px;
  line-height: 32px;
  font-size: 12px;
  font-size: 12px;
`;
const MarginTop = styled.div`
  margin-top: 16px;
`;

export default StoreTypeModal;
